import React from 'react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { adHasLightAndDarkImages, adPreviewImage } from '../utils/advertisingUtils';

const LINE_STYLE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function ThemeSwitch({ theme, onChange }) {
  const buttonStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    background: active ? '#1976d2' : 'transparent',
    color: active ? '#fff' : '#333',
  });

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      title="This ad has light and dark images"
      style={{
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 1,
        display: 'flex',
        gap: 2,
        padding: 2,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.92)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
      }}
    >
      <button
        type="button"
        aria-label="Show light image"
        aria-pressed={theme === 'light'}
        onClick={() => onChange('light')}
        style={buttonStyle(theme === 'light')}
      >
        <SunIcon style={{ width: 16, height: 16 }} />
      </button>
      <button
        type="button"
        aria-label="Show dark image"
        aria-pressed={theme === 'dark'}
        onClick={() => onChange('dark')}
        style={buttonStyle(theme === 'dark')}
      >
        <MoonIcon style={{ width: 16, height: 16 }} />
      </button>
    </div>
  );
}

/** Approximates the bottom-of-map sponsor strip in the apps. */
export default function AdBannerPreview({ ad, theme = 'light', onThemeChange }) {
  const preview = adPreviewImage(ad, theme);
  const dark = theme === 'dark';
  const showSwitch = Boolean(onThemeChange) && adHasLightAndDarkImages(ad);
  const strip = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 64,
    padding: preview?.kind === 'wide' ? 0 : '6px 12px',
    background: dark ? '#1a1a1a' : '#fff',
    color: dark ? '#f3f3f3' : '#111',
    borderBottom: dark ? '1px solid #333' : '1px solid #eee',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif',
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
  };

  let body;
  if (preview?.kind === 'wide') {
    body = (
      <img
        src={preview.src}
        alt={ad.headline || ad.name || ''}
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
    );
  } else {
    const tagline = ad.tagline || ad.message || '';
    body = (
      <>
        {preview?.src && (
          <img
            src={preview.src}
            alt=""
            style={{
              width: '30%',
              height: '100%',
              objectFit: ad.imageFit === 'centerAndFill' ? 'cover' : 'contain',
              flexShrink: 0,
              display: 'block',
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1, paddingRight: showSwitch ? 64 : 0 }}>
          <div style={{ ...LINE_STYLE, fontWeight: 700, fontSize: 13 }}>{ad.headline || ad.name || 'Headline'}</div>
          {tagline && <div style={{ ...LINE_STYLE, fontSize: 12, marginTop: 2 }}>{tagline}</div>}
        </div>
      </>
    );
  }

  return (
    <div style={strip}>
      {body}
      {showSwitch && <ThemeSwitch theme={theme} onChange={onThemeChange} />}
    </div>
  );
}
